import React, { useEffect, useRef } from "react";
import { useForm, FieldError } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  creativeCreateSchema,
  buildCreativeUpdateSchema,
  CreativeFormData,
  CreativeUpdateFormData,
} from "../../schemas/creativeSchema";
import Input from "../common/Input";
import Dropdown from "../common/Dropdown";
import Button from "../common/Button";
import Modal from "../common/Modal";
import { useAdGroupOptions } from "../../hooks/useAdGroupOptions";
import { useBrandOptions } from "../../hooks/useBrandOptions";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { formatDateForInput } from "../../utils/dateUtils";

// Unlike the filter modal there is no "All" here — this sets a value rather
// than narrowing one, and "" means the creative belongs to no brand.
const BRAND_FORM_EXTRA_OPTIONS = [{ label: "Unassigned", value: "" }];

interface CreativeFormProps {
  initialData?: any;
  onSubmit: (data: CreativeFormData | CreativeUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isOpen: boolean;
}

const CreativeForm: React.FC<CreativeFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isOpen,
}) => {
  const isUpdate = !!initialData?.id;

  const originalStartDate = formatDateForInput(initialData?.startDate);

  const defaultValues = {
    adGroupId: initialData?.adGroupId || "",
    brandId: initialData?.brandId || "",
    description: initialData?.description || "",
    startDate: originalStartDate,
    endDate: formatDateForInput(initialData?.endDate),
    ...(isUpdate ? {} : { file: undefined }),
  };

  const createForm = useForm<CreativeFormData>({
    resolver: zodResolver(creativeCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues,
  });

  // Rebuilt whenever a different creative is opened: the edit-time start-date
  // rule is relative to the value the row already carries, so the schema has to
  // be seeded with it rather than being a module-level constant.
  const updateSchema = React.useMemo(
    () => buildCreativeUpdateSchema(originalStartDate),
    [originalStartDate]
  );

  const updateForm = useForm<CreativeUpdateFormData>({
    resolver: zodResolver(updateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues,
  });

  // Track if modal was just opened to prevent resetting during submission
  const prevIsOpenRef = React.useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (isUpdate && initialData) {
        updateForm.reset({
          adGroupId: initialData?.adGroupId || "",
          brandId: initialData?.brandId || "",
          description: initialData?.description || "",
          startDate: formatDateForInput(initialData?.startDate),
          endDate: formatDateForInput(initialData?.endDate),
        });
      } else {
        createForm.reset({
          adGroupId: "",
          brandId: "",
          description: "",
          startDate: "",
          endDate: "",
          file: undefined,
        });
        // Reset the file input manually
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, isUpdate, initialData, createForm, updateForm]);

  const { adGroupOptions, isAdGroupOptionsLoading } = useAdGroupOptions();

  // Only tenants who can see brands get the field — for everyone else the
  // query would 403 and the dropdown would sit there permanently empty.
  const { canView: canViewBrands } = useAuthPermissions("brands");
  const { brandOptions, isBrandOptionsLoading } = useBrandOptions({
    enabled: canViewBrands,
  });

  if (isUpdate) {
    // Update form - no file field
    const {
      handleSubmit,
      setValue,
      formState: { errors },
      watch,
      getValues,
      trigger,
    } = updateForm;

    // Watch description to ensure we capture changes in real-time
    const watchedDescription = watch("description");

    const handleUpdateSubmit = (data: CreativeUpdateFormData) => {
      // Get current form values to ensure all changes are captured
      // This is important because react-hook-form might not include all fields in the submit data
      const currentValues = getValues();

      // Build the update payload with explicit values to ensure description is included
      // Use watched description first, then getValues, then submitted data
      const updateData: CreativeUpdateFormData = {
        adGroupId: currentValues.adGroupId || data.adGroupId,
        // `??`, not `||`: "" is a real choice here (Unassigned), so falling
        // back on it would make clearing a brand impossible.
        brandId: currentValues.brandId ?? data.brandId ?? "",
        description:
          watchedDescription !== undefined
            ? watchedDescription
            : currentValues.description !== undefined
            ? currentValues.description
            : data.description ?? "",
        startDate: currentValues.startDate || data.startDate,
        endDate: currentValues.endDate || data.endDate,
      };

      onSubmit(updateData);
    };

    // const startDate = watch("startDate");
    // const endDate = watch("endDate");

    return (
      <Modal isOpen={isOpen} onClose={onCancel} title="Edit Creative" size="md">
        <form onSubmit={handleSubmit(handleUpdateSubmit)} className="space-y-4">
          <Dropdown
            label="Ad Group"
            options={adGroupOptions || []}
            value={watch("adGroupId")}
            name="adGroupId"
            onChange={(val) => setValue("adGroupId", val as string, { shouldValidate: false })}
            onBlur={() => trigger("adGroupId")}
            error={errors.adGroupId as FieldError | undefined}
            disabled={isAdGroupOptionsLoading}
          />

          {canViewBrands && (
            <Dropdown
              label="Brand"
              options={BRAND_FORM_EXTRA_OPTIONS.concat(brandOptions)}
              value={watch("brandId") ?? ""}
              name="brandId"
              searchable
              // Dropdown treats "" as no selection, so the Unassigned option would
              // otherwise display as "Select an option" once picked.
              placeholder="Unassigned"
              onChange={(val) =>
                setValue("brandId", (val as string) ?? "", { shouldValidate: false })
              }
              onBlur={() => trigger("brandId")}
              error={errors.brandId as FieldError | undefined}
              disabled={isBrandOptionsLoading}
            />
          )}

          <Input
            label="Description"
            placeholder="Optional"
            name="description"
            value={watch("description") || ""}
            onChange={(e) => setValue("description", e.target.value, { shouldValidate: false })}
            onBlur={() => trigger("description")}
            error={errors.description as FieldError | undefined}
          />

          <Input
            label="Start Date"
            name="startDate"
            type="date"
            value={watch("startDate") || ""}
            onChange={(e) => setValue("startDate", e.target.value, { shouldValidate: false })}
            // Both dates, not just this one: the range rule reports on
            // `endDate`, so blurring only the start field would leave a stale
            // "end date must be on or after start date" showing (or hide a
            // fresh one) until the end field happened to be touched.
            onBlur={() => trigger(["startDate", "endDate"])}
            error={errors.startDate as FieldError | undefined}
          />

          <Input
            label="End Date"
            name="endDate"
            type="date"
            value={watch("endDate") || ""}
            onChange={(e) => setValue("endDate", e.target.value, { shouldValidate: false })}
            onBlur={() => trigger(["startDate", "endDate"])}
            error={errors.endDate as FieldError | undefined}
          />

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              className="w-full sm:w-auto"
            >
              Update
            </Button>
          </div>
        </form>
      </Modal>
    );
  } else {
    // Create form - with file field
    const {
      handleSubmit,
      setValue,
      formState: { errors },
      watch,
      trigger,
      clearErrors,
    } = createForm;

    const handleCreateSubmit = (data: CreativeFormData) => {
      // For creation, ensure file is included
      if (data.file && data.file.length > 0) {
        onSubmit(data);
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={onCancel}
        title="Create Creative"
        size="md"
      >
        <form onSubmit={handleSubmit(handleCreateSubmit)} className="space-y-4">
          <Dropdown
            label="Ad Group"
            options={adGroupOptions || []}
            value={watch("adGroupId")}
            name="adGroupId"
            onChange={(val) => setValue("adGroupId", val as string, { shouldValidate: false })}
            onBlur={() => trigger("adGroupId")}
            error={errors.adGroupId as FieldError | undefined}
            disabled={isAdGroupOptionsLoading}
          />

          {canViewBrands && (
            <Dropdown
              label="Brand"
              options={BRAND_FORM_EXTRA_OPTIONS.concat(brandOptions)}
              value={watch("brandId") ?? ""}
              name="brandId"
              searchable
              // Dropdown treats "" as no selection, so the Unassigned option would
              // otherwise display as "Select an option" once picked.
              placeholder="Unassigned"
              onChange={(val) =>
                setValue("brandId", (val as string) ?? "", { shouldValidate: false })
              }
              onBlur={() => trigger("brandId")}
              error={errors.brandId as FieldError | undefined}
              disabled={isBrandOptionsLoading}
            />
          )}

          <Input
            label="Description"
            placeholder="Optional"
            name="description"
            value={watch("description") || ""}
            onChange={(e) => setValue("description", e.target.value, { shouldValidate: false })}
            onBlur={() => trigger("description")}
            error={errors.description as FieldError | undefined}
          />

          <Input
            label="Start Date"
            name="startDate"
            type="date"
            value={watch("startDate") || ""}
            onChange={(e) => setValue("startDate", e.target.value, { shouldValidate: false })}
            // Both dates, not just this one: the range rule reports on
            // `endDate`, so blurring only the start field would leave a stale
            // "end date must be on or after start date" showing (or hide a
            // fresh one) until the end field happened to be touched.
            onBlur={() => trigger(["startDate", "endDate"])}
            error={errors.startDate as FieldError | undefined}
          />

          <Input
            label="End Date"
            name="endDate"
            type="date"
            value={watch("endDate") || ""}
            onChange={(e) => setValue("endDate", e.target.value, { shouldValidate: false })}
            onBlur={() => trigger(["startDate", "endDate"])}
            error={errors.endDate as FieldError | undefined}
          />

          <Input
            label="Upload File"
            name="file"
            type="file"
            multiple
            onChange={(e) => {
              // Manually set the FileList to ensure React Hook Form captures it correctly
              if (e.target.files && e.target.files.length > 0) {
                setValue("file", e.target.files as any, { shouldValidate: false });
                // Clear error immediately when file is selected
                clearErrors("file");
              } else {
                // Clear the value if no file is selected
                setValue("file", undefined as any, { shouldValidate: false });
              }
            }}
            onBlur={() => {
              // Don't trigger file validation on blur — clicking the file input
              // opens a native dialog which fires blur immediately before any
              // file is selected, causing a premature "File is required" error.
              // File validation runs on form submit instead.
            }}
            error={errors.file as any}
            ref={fileInputRef}
          />

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              className="w-full sm:w-auto"
            >
              Create
            </Button>
          </div>
        </form>
      </Modal>
    );
  }
};

export default CreativeForm;
